import { Avatar, AvatarSizes } from "@inno-ui/avatar";
import { Button, ButtonVariants } from "@inno-ui/button";
import { ChevronRightIcon, Icon, IconSizes, IconWeight } from "@inno-ui/icons";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { AvailableSlotsQueryDTO } from "../../../../api/available-slots/available-slots.types";

import { useGetAvailableTimeSlots } from "../../../../hooks/api/use-get-available-time-slots";
import { useAppointmentStore } from "../../../../store/appointment/appointment-store";
import { useGeneralStore } from "../../../../store/general/general-store";
import { formatTimezone } from "../../../../utils/helpers/date";
import { splitTextIntoLines } from "../../../../utils/helpers/general";
import { LeftSideLoading } from "../../../desktop/left-side/left-side-loading";
import MobileFooter from "../../mobile-footer/mobile-footer";
import styles from "./dashboard.module.scss";

export function Dashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setCurrentEventTypeId, setUserAppointmentData, dateToDisplay } = useAppointmentStore();
  const { currentTimezone, onNext } = useGeneralStore();
  const { data, isLoading } = useGetAvailableTimeSlots(dateToDisplay);

  useEffect(() => {
    setCurrentEventTypeId(data?.extra?.event_type?.id ?? 0);
    setUserAppointmentData(data ?? ({} as AvailableSlotsQueryDTO));
  }, [data]);

  useEffect(() => {
    if (!isLoading && (data?.code === 404 || data?.extra?.event_type?.is_active === 0)) {
      navigate("/404");
    }
  }, [isLoading, data, navigate]);

  if (isLoading) {
    return (
      <div className={styles.dashboardWrapper}>
        <LeftSideLoading avatarSize={48} />
      </div>
    );
  }

  const userData = data?.extra?.event_type;
  return (
    <div className={styles.dashboardWrapper}>
      <div className={styles.infoWrapper}>
        <Avatar borderColor="grey" size={AvatarSizes.XL} url={userData?.user_avatar} />

        <div className={styles.info}>
          <h3>{userData?.user_name}</h3>
          <span>{userData?.user_title}</span>
        </div>
      </div>

      <div className={styles.mainInfo}>
        <h2>{userData?.title}</h2>
        {/* time and meeting */}
        <div className={styles.timeAndMeeting}>
          <div>
            <Icon icon="ClockLineIcon" color="var(--gray-400)" weight={IconWeight.Bold} />
            <span>
              {userData?.duration}
              {" "}
              minutes
            </span>
          </div>
          <div>
            <Icon icon="VideocameraLineIcon" size={IconSizes.Size16} color="var(--gray-400)" weight={IconWeight.Bold} />
            <span>{userData?.platform?.title}</span>
          </div>
          <div>
            <Icon icon="GlobeLineIcon" color="var(--gray-400)" weight={IconWeight.Bold} />
            <span>{formatTimezone(currentTimezone)}</span>
          </div>
        </div>
        {/* description */}
        <div className={styles.descriptionWrapper}>
          <div className={styles.description}>
            <div>{splitTextIntoLines(userData?.description ?? "")}</div>
          </div>
          <MobileFooter
            primaryButton={(
              <Button
                variant={ButtonVariants.Dark}
                label={t("next")}
                width="100%"
                trailingIcon={ChevronRightIcon}
                onClick={onNext}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
